import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { AdminCandidates } from '../components/AdminCandidates';
import { 
  MapPin, 
  Scale, 
  FileText, 
  Users,
  Shield,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  TrendingUp,
  Eye,
  Plus,
  Edit,
  Download,
  MessageSquare,
  Quote,
  Mail,
  Phone,
  Settings,
  Link,
  Megaphone,
  Save
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusLabels = {
  urgent: { label: 'Urgent', variant: 'destructive', icon: AlertTriangle },
  en_cours: { label: 'En cours', variant: 'default', icon: Clock },
  resolu: { label: 'Résolu', variant: 'secondary', icon: CheckCircle }
};

const categoryLabels = {
  controle: 'Contrôle',
  mise_en_demeure: 'Mise en demeure',
  proces: 'Procès',
  expulsion: 'Expulsion'
};

const docCategories = [
  { id: 'juridique', label: 'Juridique' },
  { id: 'jurisprudence', label: 'Jurisprudence' },
  { id: 'guide', label: 'Guide' },
  { id: 'analyse', label: 'Analyse' },
  { id: 'bibliographie', label: 'Bibliographie' }
];

const templateCategories = [
  { id: 'mairie', label: 'Mairie' },
  { id: 'administration', label: 'Administration' },
  { id: 'prefecture', label: 'Préfecture' },
  { id: 'autre', label: 'Autre' }
];

export default function Admin() {
  const navigate = useNavigate();
  const { user, token, loading: authLoading } = useAuth();
  const [situations, setSituations] = useState([]);
  const [lawyers, setLawyers] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [contactForms, setContactForms] = useState([]);
  const [petitionSignatures, setPetitionSignatures] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const [savingSettings, setSavingSettings] = useState(false);
  const [stats, setStats] = useState({
    totalSituations: 0,
    urgentSituations: 0,
    totalLawyers: 0,
    pendingLawyers: 0,
    totalSupports: 0,
    pendingTestimonials: 0,
    pendingContacts: 0
  });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');

  // Petition filters
  const [petitionFilter, setPetitionFilter] = useState({
    search: '',
    city: '',
    newsletter: 'all',
    dateFrom: '',
    dateTo: ''
  });

  // Dialog states
  const [showDocDialog, setShowDocDialog] = useState(false);
  const [showTemplateDialog, setShowTemplateDialog] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Form states
  const [docForm, setDocForm] = useState({
    title: '', description: '', category: 'juridique', file_url: '', file_type: 'pdf'
  });
  const [templateForm, setTemplateForm] = useState({
    title: '', description: '', category: 'mairie', file_url: '', file_type: 'docx'
  });

  // Simple admin check
  const isAdmin = user?.is_admin === true;

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/connexion');
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchData();
    }
  }, [user]);

  const fetchData = async () => {
    try {
      const [situationsRes, lawyersRes, docsRes, templatesRes, testimonialsRes, contactsRes, settingsRes, petitionRes] = await Promise.all([
        axios.get(`${API}/situations`),
        axios.get(`${API}/lawyers?include_unverified=true`),
        axios.get(`${API}/documents`),
        axios.get(`${API}/templates`),
        axios.get(`${API}/testimonials?approved_only=false`),
        axios.get(`${API}/contact`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/settings`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/petition/signatures`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      setSituations(situationsRes.data);
      setLawyers(lawyersRes.data);
      setDocuments(docsRes.data);
      setTemplates(templatesRes.data);
      setTestimonials(testimonialsRes.data);
      setContactForms(contactsRes.data);
      setPetitionSignatures(petitionRes.data);
      
      // Convert settings array to object
      const settingsObj = {};
      settingsRes.data.forEach(s => { settingsObj[s.key] = s.value; });
      setSiteSettings(settingsObj);
      
      setStats({
        totalSituations: situationsRes.data.length,
        urgentSituations: situationsRes.data.filter(s => s.status === 'urgent').length,
        totalLawyers: lawyersRes.data.length,
        pendingLawyers: lawyersRes.data.filter(l => !l.verified).length,
        totalSupports: situationsRes.data.reduce((acc, s) => acc + s.support_count, 0),
        pendingTestimonials: testimonialsRes.data.filter(t => !t.approved).length,
        pendingContacts: contactsRes.data.filter(c => c.status === 'pending').length
      });
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSituation = async (situationId) => {
    if (!window.confirm('Supprimer cette situation ?')) return;
    try {
      await axios.delete(`${API}/situations/${situationId}`);
      toast.success('Situation supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleVerifyLawyer = async (lawyerId, verified) => {
    try {
      await axios.put(`${API}/lawyers/${lawyerId}/verify?verified=${verified}`);
      toast.success(verified ? 'Avocat vérifié' : 'Vérification retirée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  // Document handlers
  const openDocDialog = (doc = null) => {
    if (doc) {
      setEditingDoc(doc);
      setDocForm({
        title: doc.title,
        description: doc.description,
        category: doc.category,
        file_url: doc.file_url,
        file_type: doc.file_type
      });
    } else {
      setEditingDoc(null);
      setDocForm({ title: '', description: '', category: 'juridique', file_url: '', file_type: 'pdf' });
    }
    setShowDocDialog(true);
  };

  const handleSaveDoc = async (e) => {
    e.preventDefault();
    try {
      if (editingDoc) {
        await axios.put(`${API}/documents/${editingDoc.id}`, docForm);
        toast.success('Document modifié');
      } else {
        await axios.post(`${API}/documents`, docForm);
        toast.success('Document créé');
      }
      setShowDocDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteDoc = async (docId) => {
    if (!window.confirm('Supprimer ce document ?')) return;
    try {
      await axios.delete(`${API}/documents/${docId}`);
      toast.success('Document supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Template handlers
  const openTemplateDialog = (template = null) => {
    if (template) {
      setEditingTemplate(template);
      setTemplateForm({
        title: template.title,
        description: template.description,
        category: template.category,
        file_url: template.file_url,
        file_type: template.file_type
      });
    } else {
      setEditingTemplate(null);
      setTemplateForm({ title: '', description: '', category: 'mairie', file_url: '', file_type: 'docx' });
    }
    setShowTemplateDialog(true);
  };

  const handleSaveTemplate = async (e) => {
    e.preventDefault();
    try {
      if (editingTemplate) {
        await axios.put(`${API}/templates/${editingTemplate.id}`, templateForm);
        toast.success('Courrier modifié');
      } else {
        await axios.post(`${API}/templates`, templateForm);
        toast.success('Courrier créé');
      }
      setShowTemplateDialog(false);
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Supprimer ce courrier ?')) return;
    try {
      await axios.delete(`${API}/templates/${templateId}`);
      toast.success('Courrier supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Testimonial handlers
  const handleApproveTestimonial = async (testimonialId, approved) => {
    try {
      await axios.put(`${API}/testimonials/${testimonialId}/approve?approved=${approved}`);
      toast.success(approved ? 'Témoignage approuvé' : 'Témoignage retiré');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteTestimonial = async (testimonialId) => {
    if (!window.confirm('Supprimer ce témoignage ?')) return;
    try {
      await axios.delete(`${API}/testimonials/${testimonialId}`);
      toast.success('Témoignage supprimé');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  // Contact form handlers
  const handleUpdateContactStatus = async (contactId, status) => {
    try {
      await axios.put(`${API}/contact/${contactId}/status?status=${status}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const handleDeleteContact = async (contactId) => {
    try {
      await axios.delete(`${API}/contact/${contactId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Demande supprimée');
      fetchData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const contactReasonLabels = {
    soutien_structure: 'Soutien structuré',
    accompagnement: 'Accompagnement juridique',
    adhesion: 'Adhésion au collectif',
    partenariat: 'Partenariat',
    presse: 'Presse / Média',
    autre: 'Autre'
  };

  const contactStatusLabels = {
    pending: { label: 'En attente', variant: 'outline', icon: Clock },
    processing: { label: 'En cours', variant: 'default', icon: Eye },
    resolved: { label: 'Traité', variant: 'secondary', icon: CheckCircle }
  };

  // Settings handlers
  const handleSaveSetting = async (key, value) => {
    setSavingSettings(true);
    try {
      await axios.put(`${API}/settings/${key}`, { value }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSiteSettings(prev => ({ ...prev, [key]: value }));
      toast.success('Paramètre enregistré');
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSavingSettings(false);
    }
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Chargement...</div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="admin-unauthorized">
        <Card className="max-w-md">
          <CardContent className="py-12 text-center">
            <Shield className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
            <h2 className="text-xl font-semibold mb-2">Accès restreint</h2>
            <p className="text-muted-foreground mb-4">
              Cette page est réservée aux administrateurs.
            </p>
            <Button onClick={() => navigate('/')}>Retour à l'accueil</Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen" data-testid="admin-page">
      {/* Header */}
      <div className="bg-muted/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-primary flex items-center justify-center">
              <Shield className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-bold tracking-tight">
                Dashboard Admin
              </h1>
              <p className="text-muted-foreground">
                Gérez les situations, documents, outils et avocats.
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Overview */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <MapPin className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.totalSituations}</div>
                  <div className="text-xs text-muted-foreground">Situations</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-destructive/10 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-destructive" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.urgentSituations}</div>
                  <div className="text-xs text-muted-foreground">Urgentes</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{documents.length}</div>
                  <div className="text-xs text-muted-foreground">Documents</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                  <Download className="w-5 h-5 text-purple-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{templates.length}</div>
                  <div className="text-xs text-muted-foreground">Courriers</div>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <div className="text-2xl font-bold">{stats.totalSupports}</div>
                  <div className="text-xs text-muted-foreground">Soutiens</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-6 flex-wrap h-auto gap-2">
            <TabsTrigger value="overview" className="gap-2" data-testid="tab-overview">
              <TrendingUp className="w-4 h-4" />
              Vue d'ensemble
            </TabsTrigger>
            <TabsTrigger value="situations" className="gap-2" data-testid="tab-situations">
              <MapPin className="w-4 h-4" />
              Situations
            </TabsTrigger>
            <TabsTrigger value="documents" className="gap-2" data-testid="tab-documents">
              <FileText className="w-4 h-4" />
              Documentation
            </TabsTrigger>
            <TabsTrigger value="templates" className="gap-2" data-testid="tab-templates">
              <Download className="w-4 h-4" />
              Outils
            </TabsTrigger>
            <TabsTrigger value="lawyers" className="gap-2" data-testid="tab-lawyers">
              <Scale className="w-4 h-4" />
              Avocats
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="gap-2" data-testid="tab-testimonials">
              <Quote className="w-4 h-4" />
              Témoignages
              {stats.pendingTestimonials > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs">{stats.pendingTestimonials}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="contact" className="gap-2" data-testid="tab-contact">
              <Mail className="w-4 h-4" />
              Demandes de contact
              {stats.pendingContacts > 0 && (
                <Badge variant="destructive" className="ml-1 text-xs">{stats.pendingContacts}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="petition" className="gap-2" data-testid="tab-petition">
              <Megaphone className="w-4 h-4" />
              Pétition
              {petitionSignatures.length > 0 && (
                <Badge variant="secondary" className="ml-1 text-xs">{petitionSignatures.length}</Badge>
              )}
            </TabsTrigger>
            <TabsTrigger value="settings" className="gap-2" data-testid="tab-settings">
              <Settings className="w-4 h-4" />
              Paramètres
            </TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <MapPin className="w-5 h-5" />
                    Situations récentes
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <div className="space-y-3">
                      {[1, 2, 3].map(i => (
                        <div key={i} className="h-16 bg-muted rounded animate-pulse" />
                      ))}
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {situations.slice(0, 5).map(situation => (
                        <div key={situation.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <div className="font-medium text-sm">{situation.title}</div>
                            <div className="text-xs text-muted-foreground">{situation.location}</div>
                          </div>
                          <Badge variant={statusLabels[situation.status]?.variant}>
                            {statusLabels[situation.status]?.label}
                          </Badge>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Scale className="w-5 h-5" />
                    Avocats en attente
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {lawyers.filter(l => !l.verified).length === 0 ? (
                    <div className="text-center py-8 text-muted-foreground">
                      <CheckCircle className="w-8 h-8 mx-auto mb-2 text-green-500" />
                      Aucun avocat en attente
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {lawyers.filter(l => !l.verified).slice(0, 5).map(lawyer => (
                        <div key={lawyer.id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div>
                            <div className="font-medium text-sm">{lawyer.name}</div>
                            <div className="text-xs text-muted-foreground">{lawyer.region}</div>
                          </div>
                          <Button size="sm" onClick={() => handleVerifyLawyer(lawyer.id, true)}>
                            <CheckCircle className="w-4 h-4 mr-1" />
                            Vérifier
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Situations Tab */}
          <TabsContent value="situations">
            <Card>
              <CardHeader>
                <CardTitle>Toutes les situations ({situations.length})</CardTitle>
                <CardDescription>Gérez les situations référencées sur la carte</CardDescription>
              </CardHeader>
              <CardContent>
                {situations.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <MapPin className="w-12 h-12 mx-auto mb-4" />
                    Aucune situation référencée
                  </div>
                ) : (
                  <div className="space-y-4">
                    {situations.map(situation => (
                      <div 
                        key={situation.id} 
                        className="flex items-start justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant={statusLabels[situation.status]?.variant}>
                              {statusLabels[situation.status]?.label}
                            </Badge>
                            <Badge variant="outline">{categoryLabels[situation.category]}</Badge>
                          </div>
                          <h4 className="font-semibold">{situation.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {situation.description}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <MapPin className="w-3 h-3" />
                              {situation.location}
                            </span>
                            <span className="flex items-center gap-1">
                              <Users className="w-3 h-3" />
                              {situation.support_count} soutiens
                            </span>
                          </div>
                        </div>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDeleteSituation(situation.id)}
                        >
                          <Trash2 className="w-4 h-4 text-destructive" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Documents Tab */}
          <TabsContent value="documents">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Documentation ({documents.length})</CardTitle>
                  <CardDescription>Gérez les ressources juridiques</CardDescription>
                </div>
                <Button onClick={() => openDocDialog()} className="gap-2" data-testid="add-document-btn">
                  <Plus className="w-4 h-4" />
                  Ajouter un document
                </Button>
              </CardHeader>
              <CardContent>
                {documents.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <FileText className="w-12 h-12 mx-auto mb-4" />
                    Aucun document
                  </div>
                ) : (
                  <div className="space-y-4">
                    {documents.map(doc => (
                      <div 
                        key={doc.id} 
                        className="flex items-start justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline">{doc.category}</Badge>
                            <Badge variant="secondary">{doc.file_type.toUpperCase()}</Badge>
                          </div>
                          <h4 className="font-semibold">{doc.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {doc.description}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openDocDialog(doc)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteDoc(doc.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Templates Tab */}
          <TabsContent value="templates">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <div>
                  <CardTitle>Outils & Courriers ({templates.length})</CardTitle>
                  <CardDescription>Gérez les courriers types</CardDescription>
                </div>
                <Button onClick={() => openTemplateDialog()} className="gap-2" data-testid="add-template-btn">
                  <Plus className="w-4 h-4" />
                  Ajouter un courrier
                </Button>
              </CardHeader>
              <CardContent>
                {templates.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Download className="w-12 h-12 mx-auto mb-4" />
                    Aucun courrier
                  </div>
                ) : (
                  <div className="space-y-4">
                    {templates.map(template => (
                      <div 
                        key={template.id} 
                        className="flex items-start justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <Badge variant="outline">{template.category}</Badge>
                            <Badge variant="secondary">{template.file_type.toUpperCase()}</Badge>
                            <span className="text-xs text-muted-foreground">
                              {template.download_count} téléchargements
                            </span>
                          </div>
                          <h4 className="font-semibold">{template.title}</h4>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {template.description}
                          </p>
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => openTemplateDialog(template)}
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDeleteTemplate(template.id)}
                          >
                            <Trash2 className="w-4 h-4 text-destructive" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Lawyers Tab */}
          <TabsContent value="lawyers">
            <Card>
              <CardHeader>
                <CardTitle>Annuaire des avocats ({lawyers.length})</CardTitle>
                <CardDescription>Vérifiez et gérez les inscriptions</CardDescription>
              </CardHeader>
              <CardContent>
                {lawyers.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Scale className="w-12 h-12 mx-auto mb-4" />
                    Aucun avocat inscrit
                  </div>
                ) : (
                  <div className="space-y-4">
                    {lawyers.map(lawyer => (
                      <div 
                        key={lawyer.id} 
                        className="flex items-start justify-between p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            {lawyer.verified ? (
                              <Badge variant="secondary" className="gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Vérifié
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="gap-1 text-orange-600 border-orange-300">
                                <Clock className="w-3 h-3" />
                                En attente
                              </Badge>
                            )}
                          </div>
                          <h4 className="font-semibold">{lawyer.name}</h4>
                          <p className="text-sm text-muted-foreground">{lawyer.email}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Badge variant="outline">{lawyer.region}</Badge>
                            {lawyer.specialties.map(spec => (
                              <Badge key={spec} variant="secondary" className="text-xs">
                                {spec}
                              </Badge>
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-muted-foreground">Vérifié</span>
                          <Switch
                            checked={lawyer.verified}
                            onCheckedChange={(checked) => handleVerifyLawyer(lawyer.id, checked)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Quote className="w-5 h-5" />
                  Gestion des témoignages ({testimonials.length})
                </CardTitle>
                <CardDescription>
                  Modérez les témoignages soumis par les utilisateurs avant publication
                </CardDescription>
              </CardHeader>
              <CardContent>
                {testimonials.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Quote className="w-12 h-12 mx-auto mb-4" />
                    Aucun témoignage à modérer
                  </div>
                ) : (
                  <div className="space-y-4">
                    {testimonials.map(testimonial => (
                      <div 
                        key={testimonial.id} 
                        className="p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                        data-testid={`testimonial-${testimonial.id}`}
                      >
                        <div className="flex items-start justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-2">
                              {testimonial.approved ? (
                                <Badge variant="secondary" className="gap-1">
                                  <CheckCircle className="w-3 h-3" />
                                  Approuvé
                                </Badge>
                              ) : (
                                <Badge variant="outline" className="gap-1 text-orange-600 border-orange-300">
                                  <Clock className="w-3 h-3" />
                                  En attente
                                </Badge>
                              )}
                              <Badge variant="outline">{testimonial.habitat_type}</Badge>
                              <Badge variant="secondary">{testimonial.location}</Badge>
                              {testimonial.anonymous && (
                                <Badge variant="destructive" className="text-xs">Anonyme</Badge>
                              )}
                            </div>
                            <h4 className="font-semibold text-lg">
                              {testimonial.anonymous ? 'Anonyme' : testimonial.name}
                            </h4>
                            <p className="text-sm text-muted-foreground mb-2">
                              {testimonial.situation_summary}
                            </p>
                            <p className="text-sm bg-muted/50 p-3 rounded-lg italic">
                              "{testimonial.testimonial}"
                            </p>
                            {testimonial.outcome && (
                              <p className="text-sm mt-2">
                                <span className="font-medium text-green-600">Issue : </span>
                                {testimonial.outcome}
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground mt-2">
                              Soumis le {new Date(testimonial.created_at).toLocaleDateString('fr-FR')}
                            </p>
                          </div>
                          <div className="flex flex-col gap-2">
                            {!testimonial.approved ? (
                              <Button 
                                size="sm" 
                                onClick={() => handleApproveTestimonial(testimonial.id, true)}
                                data-testid={`approve-testimonial-${testimonial.id}`}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                Approuver
                              </Button>
                            ) : (
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => handleApproveTestimonial(testimonial.id, false)}
                                data-testid={`unapprove-testimonial-${testimonial.id}`}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                Retirer
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="ghost"
                              onClick={() => handleDeleteTestimonial(testimonial.id)}
                              data-testid={`delete-testimonial-${testimonial.id}`}
                            >
                              <Trash2 className="w-4 h-4 text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Contact Forms Tab */}
          <TabsContent value="contact">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Mail className="w-5 h-5" />
                  Demandes de contact ({contactForms.length})
                </CardTitle>
                <CardDescription>
                  Gérez les demandes de contact reçues via le formulaire
                </CardDescription>
              </CardHeader>
              <CardContent>
                {contactForms.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <Mail className="w-12 h-12 mx-auto mb-4" />
                    Aucune demande de contact
                  </div>
                ) : (
                  <div className="space-y-4">
                    {contactForms.map(contact => {
                      const StatusIcon = contactStatusLabels[contact.status]?.icon || Clock;
                      return (
                        <div 
                          key={contact.id} 
                          className="p-4 border rounded-lg hover:bg-muted/30 transition-colors"
                          data-testid={`contact-${contact.id}`}
                        >
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2 flex-wrap">
                                <Badge variant={contactStatusLabels[contact.status]?.variant || 'outline'} className="gap-1">
                                  <StatusIcon className="w-3 h-3" />
                                  {contactStatusLabels[contact.status]?.label || contact.status}
                                </Badge>
                                <Badge variant="secondary">
                                  {contactReasonLabels[contact.reason] || contact.reason}
                                </Badge>
                              </div>
                              <h4 className="font-semibold text-lg">{contact.subject}</h4>
                              <div className="flex items-center gap-4 text-sm text-muted-foreground mt-1 flex-wrap">
                                <span className="flex items-center gap-1">
                                  <Users className="w-3 h-3" />
                                  {contact.name}
                                </span>
                                <a href={`mailto:${contact.email}`} className="flex items-center gap-1 hover:text-primary">
                                  <Mail className="w-3 h-3" />
                                  {contact.email}
                                </a>
                                {contact.phone && (
                                  <span className="flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {contact.phone}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm mt-3 bg-muted/50 p-3 rounded-lg whitespace-pre-wrap">
                                {contact.message}
                              </p>
                              <p className="text-xs text-muted-foreground mt-2">
                                Reçu le {new Date(contact.created_at).toLocaleDateString('fr-FR', {
                                  day: 'numeric',
                                  month: 'long',
                                  year: 'numeric',
                                  hour: '2-digit',
                                  minute: '2-digit'
                                })}
                              </p>
                            </div>
                            <div className="flex flex-col gap-2">
                              {contact.status === 'pending' && (
                                <Button 
                                  size="sm" 
                                  onClick={() => handleUpdateContactStatus(contact.id, 'processing')}
                                  data-testid={`process-contact-${contact.id}`}
                                >
                                  <Eye className="w-4 h-4 mr-1" />
                                  En cours
                                </Button>
                              )}
                              {contact.status === 'processing' && (
                                <Button 
                                  size="sm"
                                  variant="secondary"
                                  onClick={() => handleUpdateContactStatus(contact.id, 'resolved')}
                                  data-testid={`resolve-contact-${contact.id}`}
                                >
                                  <CheckCircle className="w-4 h-4 mr-1" />
                                  Traité
                                </Button>
                              )}
                              {contact.status === 'resolved' && (
                                <Button 
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleUpdateContactStatus(contact.id, 'pending')}
                                  data-testid={`reopen-contact-${contact.id}`}
                                >
                                  <Clock className="w-4 h-4 mr-1" />
                                  Réouvrir
                                </Button>
                              )}
                              <Button 
                                size="sm" 
                                variant="ghost"
                                onClick={() => handleDeleteContact(contact.id)}
                                data-testid={`delete-contact-${contact.id}`}
                              >
                                <Trash2 className="w-4 h-4 text-destructive" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Petition Tab */}
          <TabsContent value="petition">
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Megaphone className="w-5 h-5" />
                      Signataires de la pétition
                    </CardTitle>
                    <CardDescription className="mt-1">
                      {petitionSignatures.length} signature{petitionSignatures.length > 1 ? 's' : ''} enregistrée{petitionSignatures.length > 1 ? 's' : ''} en base
                      {' · '}
                      <a href="/petition-habitat-leger-2027" target="_blank" rel="noopener noreferrer" className="underline">
                        Voir la page publique
                      </a>
                    </CardDescription>
                  </div>
                  <Button
                    onClick={async () => {
                      try {
                        const res = await axios.get(`${API}/petition/signatures/export`, {
                          headers: { Authorization: `Bearer ${token}` },
                          responseType: 'blob'
                        });
                        const url = window.URL.createObjectURL(new Blob([res.data]));
                        const link = document.createElement('a');
                        link.href = url;
                        link.setAttribute('download', `signatures-petition-${new Date().toISOString().slice(0,10)}.csv`);
                        document.body.appendChild(link);
                        link.click();
                        link.remove();
                        window.URL.revokeObjectURL(url);
                        toast.success('Export CSV téléchargé');
                      } catch {
                        toast.error("Erreur lors de l'export");
                      }
                    }}
                    data-testid="export-petition-csv-btn"
                    className="gap-2"
                  >
                    <Download className="w-4 h-4" />
                    Exporter en CSV
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                {/* Filters */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3 mb-6 p-4 rounded-lg bg-muted/40 border border-border">
                  <div className="md:col-span-2">
                    <Label className="text-xs text-muted-foreground">Recherche</Label>
                    <Input
                      placeholder="Nom, email, commentaire…"
                      value={petitionFilter.search}
                      onChange={(e) => setPetitionFilter({ ...petitionFilter, search: e.target.value })}
                      data-testid="petition-filter-search"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Ville / Code postal</Label>
                    <Input
                      placeholder="Paris, 75001…"
                      value={petitionFilter.city}
                      onChange={(e) => setPetitionFilter({ ...petitionFilter, city: e.target.value })}
                      data-testid="petition-filter-city"
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Newsletter</Label>
                    <Select
                      value={petitionFilter.newsletter}
                      onValueChange={(v) => setPetitionFilter({ ...petitionFilter, newsletter: v })}
                    >
                      <SelectTrigger className="mt-1" data-testid="petition-filter-newsletter">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Toutes</SelectItem>
                        <SelectItem value="yes">Abonné·e·s uniquement</SelectItem>
                        <SelectItem value="no">Non abonné·e·s</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-2 md:col-span-1">
                    <div>
                      <Label className="text-xs text-muted-foreground">Du</Label>
                      <Input
                        type="date"
                        value={petitionFilter.dateFrom}
                        onChange={(e) => setPetitionFilter({ ...petitionFilter, dateFrom: e.target.value })}
                        data-testid="petition-filter-date-from"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-muted-foreground">Au</Label>
                      <Input
                        type="date"
                        value={petitionFilter.dateTo}
                        onChange={(e) => setPetitionFilter({ ...petitionFilter, dateTo: e.target.value })}
                        data-testid="petition-filter-date-to"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  {(petitionFilter.search || petitionFilter.city || petitionFilter.newsletter !== 'all' || petitionFilter.dateFrom || petitionFilter.dateTo) && (
                    <div className="md:col-span-5 flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setPetitionFilter({ search: '', city: '', newsletter: 'all', dateFrom: '', dateTo: '' })}
                        data-testid="petition-filter-reset"
                      >
                        Réinitialiser les filtres
                      </Button>
                    </div>
                  )}
                </div>

                {(() => {
                  const filtered = petitionSignatures.filter((s) => {
                    const q = petitionFilter.search.trim().toLowerCase();
                    if (q) {
                      const hay = [s.name, s.email, s.comment, s.city, s.postal_code].filter(Boolean).join(' ').toLowerCase();
                      if (!hay.includes(q)) return false;
                    }
                    const cityQ = petitionFilter.city.trim().toLowerCase();
                    if (cityQ) {
                      const cityHay = [s.city, s.postal_code].filter(Boolean).join(' ').toLowerCase();
                      if (!cityHay.includes(cityQ)) return false;
                    }
                    if (petitionFilter.newsletter === 'yes' && !s.newsletter) return false;
                    if (petitionFilter.newsletter === 'no' && s.newsletter) return false;
                    if (petitionFilter.dateFrom) {
                      if (new Date(s.created_at) < new Date(petitionFilter.dateFrom)) return false;
                    }
                    if (petitionFilter.dateTo) {
                      const end = new Date(petitionFilter.dateTo);
                      end.setHours(23, 59, 59, 999);
                      if (new Date(s.created_at) > end) return false;
                    }
                    return true;
                  });

                  if (petitionSignatures.length === 0) {
                    return (
                      <div className="text-center py-12 text-muted-foreground" data-testid="petition-empty">
                        Aucune signature pour le moment
                      </div>
                    );
                  }

                  return (
                    <>
                      <div className="text-xs text-muted-foreground mb-3" data-testid="petition-filtered-count">
                        {filtered.length} résultat{filtered.length > 1 ? 's' : ''} affiché{filtered.length > 1 ? 's' : ''} sur {petitionSignatures.length}
                      </div>
                      {filtered.length === 0 ? (
                        <div className="text-center py-12 text-muted-foreground">
                          Aucun signataire ne correspond aux filtres
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm" data-testid="petition-signatures-table">
                            <thead>
                              <tr className="border-b border-border text-left">
                                <th className="py-2 pr-3 font-medium">Date</th>
                                <th className="py-2 pr-3 font-medium">Nom</th>
                                <th className="py-2 pr-3 font-medium">Email</th>
                                <th className="py-2 pr-3 font-medium">Ville</th>
                                <th className="py-2 pr-3 font-medium">Commentaire</th>
                                <th className="py-2 pr-3 font-medium">News.</th>
                                <th className="py-2 pr-3 font-medium">Public</th>
                              </tr>
                            </thead>
                            <tbody>
                              {filtered.map((s) => (
                                <tr key={s.id} className="border-b border-border/50 hover:bg-muted/30">
                                  <td className="py-2 pr-3 text-muted-foreground whitespace-nowrap">
                                    {new Date(s.created_at).toLocaleDateString('fr-FR')}
                                  </td>
                                  <td className="py-2 pr-3 font-medium">{s.name}</td>
                                  <td className="py-2 pr-3 text-muted-foreground">{s.email}</td>
                                  <td className="py-2 pr-3 text-muted-foreground">
                                    {[s.postal_code, s.city].filter(Boolean).join(' ') || '—'}
                                  </td>
                                  <td className="py-2 pr-3 text-muted-foreground max-w-xs truncate">
                                    {s.comment || '—'}
                                  </td>
                                  <td className="py-2 pr-3">
                                    {s.newsletter ? (
                                      <Badge variant="secondary" className="text-xs">oui</Badge>
                                    ) : (
                                      <span className="text-muted-foreground text-xs">non</span>
                                    )}
                                  </td>
                                  <td className="py-2 pr-3">
                                    {s.public_display ? (
                                      <Badge variant="secondary" className="text-xs">oui</Badge>
                                    ) : (
                                      <span className="text-muted-foreground text-xs">non</span>
                                    )}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </>
                  );
                })()}
              </CardContent>
            </Card>
            <AdminCandidates token={token} />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Settings className="w-5 h-5" />
                  Paramètres du site
                </CardTitle>
                <CardDescription>
                  Configurez les liens et paramètres globaux du site
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Guide Link Setting */}
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Link className="w-4 h-4 text-primary" />
                    <Label htmlFor="guide-link" className="text-base font-medium">
                      Lien du Guide de défense juridique
                    </Label>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Ce lien est affiché sur la page Documentation. Mettez <code className="bg-muted px-1 rounded">#</code> pour afficher "Disponible prochainement".
                  </p>
                  <div className="flex gap-2">
                    <Input
                      id="guide-link"
                      value={siteSettings.guide_link || ''}
                      onChange={(e) => setSiteSettings(prev => ({ ...prev, guide_link: e.target.value }))}
                      placeholder="https://... ou #"
                      className="flex-1"
                      data-testid="guide-link-input"
                    />
                    <Button 
                      onClick={() => handleSaveSetting('guide_link', siteSettings.guide_link || '#')}
                      disabled={savingSettings}
                      data-testid="save-guide-link-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      Enregistrer
                    </Button>
                  </div>
                  <div className="text-sm">
                    <span className="text-muted-foreground">Aperçu : </span>
                    {siteSettings.guide_link && siteSettings.guide_link !== '#' ? (
                      <span className="text-green-600">✓ Le bouton "Consulter le guide" sera affiché</span>
                    ) : (
                      <span className="text-orange-600">⏳ Le bouton "Disponible prochainement" sera affiché</span>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Document Dialog */}
      <Dialog open={showDocDialog} onOpenChange={setShowDocDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingDoc ? 'Modifier le document' : 'Ajouter un document'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveDoc} className="space-y-4">
            <div>
              <Label htmlFor="doc-title">Titre *</Label>
              <Input
                id="doc-title"
                value={docForm.title}
                onChange={(e) => setDocForm({ ...docForm, title: e.target.value })}
                required
                data-testid="doc-title-input"
              />
            </div>
            <div>
              <Label htmlFor="doc-description">Description *</Label>
              <Textarea
                id="doc-description"
                value={docForm.description}
                onChange={(e) => setDocForm({ ...docForm, description: e.target.value })}
                required
                data-testid="doc-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Catégorie</Label>
                <Select value={docForm.category} onValueChange={(v) => setDocForm({ ...docForm, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {docCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type de fichier</Label>
                <Select value={docForm.file_type} onValueChange={(v) => setDocForm({ ...docForm, file_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="docx">DOCX</SelectItem>
                    <SelectItem value="external">Lien externe</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="doc-url">URL du fichier *</Label>
              <Input
                id="doc-url"
                value={docForm.file_url}
                onChange={(e) => setDocForm({ ...docForm, file_url: e.target.value })}
                placeholder="https://..."
                required
                data-testid="doc-url-input"
              />
            </div>
            <Button type="submit" className="w-full" data-testid="save-doc-btn">
              {editingDoc ? 'Modifier' : 'Créer'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Template Dialog */}
      <Dialog open={showTemplateDialog} onOpenChange={setShowTemplateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTemplate ? 'Modifier le courrier' : 'Ajouter un courrier'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSaveTemplate} className="space-y-4">
            <div>
              <Label htmlFor="template-title">Titre *</Label>
              <Input
                id="template-title"
                value={templateForm.title}
                onChange={(e) => setTemplateForm({ ...templateForm, title: e.target.value })}
                required
                data-testid="template-title-input"
              />
            </div>
            <div>
              <Label htmlFor="template-description">Description *</Label>
              <Textarea
                id="template-description"
                value={templateForm.description}
                onChange={(e) => setTemplateForm({ ...templateForm, description: e.target.value })}
                required
                data-testid="template-description-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Catégorie</Label>
                <Select value={templateForm.category} onValueChange={(v) => setTemplateForm({ ...templateForm, category: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {templateCategories.map(cat => (
                      <SelectItem key={cat.id} value={cat.id}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Type de fichier</Label>
                <Select value={templateForm.file_type} onValueChange={(v) => setTemplateForm({ ...templateForm, file_type: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="docx">DOCX</SelectItem>
                    <SelectItem value="pdf">PDF</SelectItem>
                    <SelectItem value="odt">ODT</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label htmlFor="template-url">URL du fichier *</Label>
              <Input
                id="template-url"
                value={templateForm.file_url}
                onChange={(e) => setTemplateForm({ ...templateForm, file_url: e.target.value })}
                placeholder="https://..."
                required
                data-testid="template-url-input"
              />
            </div>
            <Button type="submit" className="w-full" data-testid="save-template-btn">
              {editingTemplate ? 'Modifier' : 'Créer'}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
